(() => {
  if (customElements.get('cam-account-login')) return;
  const storage = {
    read(type, key) { try { return window[type].getItem(key); } catch { return null; } },
    write(type, key, value) { try { window[type].setItem(key, value); } catch {} }
  };
  const normalisePhone = raw => {
    let digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
    if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
    return /^[6-9]\d{9}$/.test(digits) ? digits : '';
  };
  // Numbers outside India: digits only, 6-14 long, without leading zeros.
  const normaliseIntl = raw => {
    const digits = String(raw || '').replace(/\D/g, '').replace(/^0+/, '');
    return /^\d{6,14}$/.test(digits) ? digits : '';
  };
  customElements.define('cam-account-login', class extends HTMLElement {
    // Locks page scrolling while any popup of this element is open (the login popup can hand over to the coupon popup).
    syncScroll(force) {
      const open = !force && this.querySelector('dialog[open]');
      if (open && !this.locked) {
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.locked = true;
      } else if (!open && this.locked) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.locked = false;
      }
    }
    // Coupon unlocked popup with party poppers, shown after a number is saved or once after a new customer signs in.
    setupReward(options, editor) {
      const reward = this.querySelector('.cam-reward-dialog');
      if (!reward || typeof reward.showModal !== 'function') return;
      const close = () => { if (reward.open) reward.close(); this.syncScroll(); };
      reward.querySelector('.cam-reward-close').addEventListener('click', close, options);
      reward.querySelector('[data-reward-close]')?.addEventListener('click', close, options);
      reward.addEventListener('close', () => {
        this.querySelector('.cam-reward-poppers').replaceChildren();
        reward.querySelector('.cam-reward-confetti')?.remove();
        this.syncScroll();
      }, options);
      reward.addEventListener('click', event => {
        if (this.dataset.backdropClose !== 'true' || event.target !== reward) return;
        const bounds = reward.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
      }, options);
      this.showReward = () => {
        if (reward.open) return;
        if (!editor) storage.write('localStorage', 'cam_reward_seen', '1');
        reward.showModal();
        this.syncScroll();
        if (this.dataset.rewardConfetti === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.celebrate(reward);
      };
      this.closeReward = close;
      // Start loading the artwork early so the coupon popup never opens without its background.
      this.preloadReward = () => reward.querySelector('.cam-reward-bg')?.setAttribute('loading', 'eager');
      if (!editor && this.dataset.rewardLogin === 'true' && !storage.read('localStorage', 'cam_reward_seen')) {
        this.preloadReward();
        const attempt = () => {
          if (!this.isConnected) return;
          if (document.hidden || document.querySelector('dialog[open]')) {
            this.rewardTimer = setTimeout(attempt, 1000);
            return;
          }
          this.showReward();
        };
        this.rewardTimer = setTimeout(attempt, 1200);
      }
    }
    celebrate(reward) {
      const card = reward.querySelector('.cam-reward-card');
      const poppers = reward.querySelector('.cam-reward-poppers');
      poppers.innerHTML = '<b class="cam-reward-popper cam-reward-popper--left">&#127881;</b><b class="cam-reward-popper cam-reward-popper--right">&#127881;</b>';
      reward.querySelector('.cam-reward-confetti')?.remove();
      const box = card.getBoundingClientRect(), w = box.width, h = box.height, ratio = Math.min(window.devicePixelRatio || 1, 2);
      if (!w || !h) return;
      const canvas = document.createElement('canvas');
      canvas.className = 'cam-reward-confetti';
      canvas.setAttribute('aria-hidden', 'true');
      canvas.width = w * ratio;
      canvas.height = h * ratio;
      card.appendChild(canvas);
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);
      const colours = ['#9d2549', '#e5b23d', '#f6c344', '#e88099', '#ff7a1a', '#4f926b', '#8f78b5', '#2f8fd8'];
      const bits = [];
      for (let side = 0; side < 2; side++) {
        for (let i = 0; i < 80; i++) {
          const angle = (side ? -118 : -62) + (Math.random() - 0.5) * 36, speed = (8 + Math.random() * 9) * Math.max(0.7, w / 480), rad = angle * Math.PI / 180;
          bits.push({ x: side ? w - 30 : 30, y: h - 40, vx: Math.cos(rad) * speed, vy: Math.sin(rad) * speed, size: 5 + Math.random() * 6, turn: Math.random() * 6.28, spin: (Math.random() - 0.5) * 0.35, wobble: Math.random() * 6.28, shape: i % 3, colour: colours[(i + side) % colours.length] });
        }
      }
      for (let i = 0; i < 50; i++) bits.push({ x: Math.random() * w, y: -20 - Math.random() * h * 0.5, vx: (Math.random() - 0.5) * 2, vy: 2 + Math.random() * 3, size: 5 + Math.random() * 5, turn: Math.random() * 6.28, spin: (Math.random() - 0.5) * 0.3, wobble: Math.random() * 6.28, shape: i % 3, colour: colours[i % colours.length] });
      const start = performance.now();
      const frame = now => {
        const age = now - start;
        ctx.clearRect(0, 0, w, h);
        ctx.globalAlpha = age > 2600 ? Math.max(0, 1 - (age - 2600) / 700) : 1;
        for (const b of bits) {
          b.vx *= 0.985;
          b.vy = b.vy * 0.985 + 0.28;
          b.x += b.vx + Math.sin(b.wobble += 0.1) * 0.6;
          b.y += b.vy;
          b.turn += b.spin;
          ctx.save();
          ctx.translate(b.x, b.y);
          ctx.rotate(b.turn);
          ctx.fillStyle = b.colour;
          if (b.shape === 0) ctx.fillRect(-b.size / 2, -b.size * 0.35, b.size, b.size * 0.7);
          else if (b.shape === 1) { ctx.beginPath(); ctx.arc(0, 0, b.size * 0.38, 0, 6.28); ctx.fill(); }
          else ctx.fillRect(-b.size * 0.15, -b.size, b.size * 0.3, b.size * 2);
          ctx.restore();
        }
        if (age < 3300 && canvas.isConnected) requestAnimationFrame(frame);
        else canvas.remove();
      };
      requestAnimationFrame(frame);
    }
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const editor = this.dataset.editor === 'true';
      this.setupReward(options, editor);
      this.cleanupModal = () => this.syncScroll(true);
      if (editor && this.showReward) {
        document.addEventListener('shopify:block:select', event => {
          if (event.detail.blockId === this.dataset.blockId && this.dataset.rewardPreview === 'true') this.showReward();
        }, options);
        document.addEventListener('shopify:block:deselect', event => {
          if (event.detail.blockId === this.dataset.blockId) this.closeReward();
        }, options);
        if (this.dataset.rewardPreview === 'true') this.showReward();
      }
      const trigger = this.querySelector('.cam-account-icon');
      const dialog = this.querySelector('.cam-account-dialog');
      if (!trigger || !dialog || typeof dialog.showModal !== 'function') return;
      const key = 'cam_popup_' + this.dataset.campaign;
      const frequency = this.dataset.frequency || 'days';
      const seen = () => {
        if (frequency === 'page') return false;
        if (frequency === 'once') return !!storage.read('localStorage', key);
        // Once per visit: not again while moving between pages, but again after a refresh or when the site is reopened.
        if (frequency === 'visit') {
          const navigation = performance.getEntriesByType?.('navigation')?.[0];
          if (navigation && navigation.type === 'reload' && !this.shownThisLoad) return false;
          return !!storage.read('sessionStorage', key);
        }
        if (frequency === 'session') return !!storage.read('sessionStorage', key);
        const timestamp = Number(storage.read('localStorage', key));
        return timestamp > 0 && Date.now() - timestamp < Number(this.dataset.repeatDays || 7) * 86400000;
      };
      const markSeen = () => {
        if (editor) return;
        storage.write('sessionStorage', key, '1');
        storage.write('localStorage', key, String(Date.now()));
      };
      const open = () => {
        if (dialog.open) return;
        clearTimeout(this.autoTimer);
        this.returnFocus = document.activeElement;
        dialog.showModal();
        this.syncScroll();
        this.shownThisLoad = true;
        this.preloadReward?.();
        markSeen();
      };
      const close = () => { if (dialog.open) dialog.close(); this.syncScroll(); };
      trigger.addEventListener('click', event => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        // Visitors who already saved their number see their account panel (or, if it is turned off, their Shopify account) instead of the popup.
        if (!editor && storage.read('localStorage', 'cam_phone')) {
          if (this.openPanel) this.openPanel();
          else if (this.dataset.accountUrl) window.location.href = this.dataset.accountUrl;
          return;
        }
        open();
      }, options);
      dialog.querySelector('.cam-account-close').addEventListener('click', close, options);
      dialog.addEventListener('close', () => {
        this.syncScroll();
        const focus = this.returnFocus;
        if (focus && focus.isConnected && focus !== document.body) focus.focus({ preventScroll: true });
      }, options);
      dialog.addEventListener('click', event => {
        if (this.dataset.backdropClose !== 'true' || event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
      }, options);
      const form = dialog.querySelector('[data-phone-form]');
      const input = form.elements.phone;
      const error = form.querySelector('.cam-account-error');
      const invalidMessage = error.textContent;
      const ccSelect = form.elements.country_code;
      const ccDisplay = form.querySelector('[data-cc-display]');
      const ccFlag = form.querySelector('[data-cc-flag]');
      const countryCode = () => (ccSelect ? ccSelect.value : '') || this.dataset.countryCode || '+91';
      const isIndia = () => countryCode() === '+91';
      const normalise = value => (isIndia() ? normalisePhone(value) : normaliseIntl(value));
      const syncCountry = () => {
        if (ccDisplay) ccDisplay.textContent = countryCode();
        if (ccFlag) ccFlag.remove();
        input.placeholder = isIndia() ? input.dataset.placeholderIn : input.dataset.placeholderIntl;
      };
      const savedCode = storage.read('localStorage', 'cam_phone_cc');
      if (ccSelect && savedCode && [...ccSelect.options].some(option => option.value === savedCode)) ccSelect.value = savedCode;
      syncCountry();
      ccSelect?.addEventListener('change', () => {
        syncCountry();
        error.hidden = true;
        input.removeAttribute('aria-invalid');
      }, options);
      const saved = storage.read('localStorage', 'cam_phone');
      if (saved) input.value = normalise(saved);
      input.addEventListener('input', () => {
        error.hidden = true;
        input.removeAttribute('aria-invalid');
      }, options);
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (form.getAttribute('aria-busy') === 'true') return;
        const phone = normalise(input.value);
        if (!phone) {
          error.textContent = isIndia() ? invalidMessage : error.dataset.errorIntl;
          error.hidden = false;
          input.setAttribute('aria-invalid', 'true');
          input.focus();
          return;
        }
        // Lead capture: the number also goes to the store's private database; a failure never blocks the popup.
        const leadEndpoint = this.dataset.leadEndpoint;
        if (leadEndpoint && !editor) {
          try {
            fetch(leadEndpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'text/plain' },
              body: JSON.stringify({ type: 'lead', country_code: countryCode(), number: phone, page: location.pathname }),
              keepalive: true
            }).catch(() => {});
          } catch {}
        }
        const button = form.querySelector('button[type="submit"]');
        error.hidden = true;
        input.removeAttribute('aria-invalid');
        button.disabled = true;
        form.setAttribute('aria-busy', 'true');
        this.requestController = new AbortController();
        const timeout = setTimeout(() => this.requestController?.abort(), 10000);
        try {
          const response = await fetch(this.dataset.cartUpdate, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { 'Mobile number': countryCode() + ' ' + phone } }),
            signal: this.requestController.signal
          });
          if (!response.ok) throw new Error('Save failed');
          storage.write('localStorage', 'cam_phone', phone);
          storage.write('localStorage', 'cam_phone_cc', countryCode());
          input.value = phone;
          close();
          if (!this.changingNumber) this.showReward?.();
          this.changingNumber = false;
        } catch {
          if (this.isConnected) {
            error.textContent = this.dataset.saveError;
            error.hidden = false;
          }
        } finally {
          clearTimeout(timeout);
          button.disabled = false;
          form.removeAttribute('aria-busy');
        }
      }, options);
      const panel = this.querySelector('.cam-panel-dialog');
      if (panel && typeof panel.showModal === 'function') {
        const closePanel = () => { if (panel.open) panel.close(); this.syncScroll(); };
        const bag = panel.querySelector('[data-panel-bag]');
        const money = (cents, currency) => {
          try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR' }).format(cents / 100); } catch { return (cents / 100).toFixed(2); }
        };
        const note = text => {
          const p = document.createElement('p');
          p.className = 'cam-panel-muted';
          p.textContent = text;
          return p;
        };
        const paintBag = cart => {
          if (!cart || !cart.items || !cart.items.length) {
            bag.replaceChildren(note(bag.dataset.empty));
            return;
          }
          const list = document.createElement('ul');
          list.className = 'cam-panel-items';
          cart.items.slice(0, 3).forEach(item => {
            const row = document.createElement('li');
            if (item.image) {
              const img = document.createElement('img');
              img.src = item.image + (item.image.includes('?') ? '&' : '?') + 'width=120';
              img.alt = '';
              img.width = 48;
              img.height = 48;
              img.loading = 'lazy';
              row.appendChild(img);
            }
            const info = document.createElement('div');
            const name = document.createElement('span');
            name.className = 'cam-panel-item-name';
            name.textContent = item.product_title + (item.variant_title ? ' - ' + item.variant_title : '');
            const meta = document.createElement('span');
            meta.className = 'cam-panel-item-meta';
            meta.textContent = '× ' + item.quantity + '  ·  ' + money(item.final_line_price, cart.currency);
            info.append(name, meta);
            row.appendChild(info);
            list.appendChild(row);
          });
          const parts = [list];
          if (cart.items.length > 3) parts.push(note((bag.dataset.more || '+ [count] more').replace('[count]', cart.items.length - 3)));
          const total = document.createElement('p');
          total.className = 'cam-panel-total';
          total.textContent = (bag.dataset.total || 'Total') + ': ' + money(cart.total_price, cart.currency);
          parts.push(total);
          bag.replaceChildren(...parts);
        };
        this.openPanel = async () => {
          clearTimeout(this.autoTimer);
          const number = storage.read('localStorage', 'cam_phone');
          const code = storage.read('localStorage', 'cam_phone_cc') || this.dataset.countryCode || '+91';
          panel.querySelector('[data-panel-number]').textContent = number ? code + ' ' + number : (editor ? '+91 9876543210' : '');
          if (!panel.open) {
            panel.showModal();
            this.syncScroll();
          }
          try {
            const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } });
            if (response.ok) paintBag(await response.json());
          } catch {}
        };
        panel.querySelector('.cam-panel-close').addEventListener('click', closePanel, options);
        panel.addEventListener('close', () => this.syncScroll(), options);
        panel.addEventListener('click', event => {
          if (this.dataset.backdropClose !== 'true' || event.target !== panel) return;
          const bounds = panel.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closePanel();
        }, options);
        // The bag button also carries data-cart-toggle, so the theme opens the bag after this closes the panel.
        panel.querySelector('[data-panel-bag-button]')?.addEventListener('click', closePanel, options);
        panel.querySelector('[data-panel-change]')?.addEventListener('click', () => {
          closePanel();
          this.changingNumber = true;
          open();
          input.focus();
        }, options);
        panel.querySelector('[data-panel-logout]')?.addEventListener('click', () => {
          try { window.localStorage.removeItem('cam_phone'); window.localStorage.removeItem('cam_phone_cc'); } catch {}
          input.value = '';
          closePanel();
          if (!editor) {
            // Also remove the number from this browser's bag so the next person on this device does not inherit it.
            fetch(this.dataset.cartUpdate, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ attributes: { 'Mobile number': '' } })
            }).catch(() => {});
          }
        }, options);
      }
      if (editor) {
        const otherPreview = () => this.dataset.rewardPreview === 'true' || (this.openPanel && this.dataset.panelPreview === 'true');
        document.addEventListener('shopify:block:select', event => {
          if (event.detail.blockId !== this.dataset.blockId) return;
          if (this.openPanel && this.dataset.panelPreview === 'true' && this.dataset.rewardPreview !== 'true') this.openPanel();
          else if (!otherPreview()) open();
        }, options);
        document.addEventListener('shopify:block:deselect', event => {
          if (event.detail.blockId === this.dataset.blockId) {
            close();
            if (panel?.open) { panel.close(); this.syncScroll(); }
          }
        }, options);
        if (this.openPanel && this.dataset.panelPreview === 'true' && this.dataset.rewardPreview !== 'true') this.openPanel();
        else if (this.dataset.preview === 'true' && !otherPreview()) open();
      } else {
        const delay = Number(this.dataset.autoshow);
        const skipSaved = saved && this.dataset.skipSaved !== 'false';
        if (Number.isFinite(delay) && delay > 0 && !seen() && !skipSaved) {
          const attempt = () => {
            if (!this.isConnected || seen()) return;
            if (document.hidden || document.querySelector('dialog[open]')) {
              this.autoTimer = setTimeout(attempt, 1000);
              return;
            }
            open();
          };
          this.autoTimer = setTimeout(attempt, delay * 1000);
        }
      }
    }
    disconnectedCallback() {
      clearTimeout(this.autoTimer);
      clearTimeout(this.rewardTimer);
      this.requestController?.abort();
      this.cleanupModal?.();
      this.controller?.abort();
      this.controller = null;
    }
  });
})();



